import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';
import { BIMElement, DuctElement, PipeElement } from './types';
import { AutoRoutingEngine, RoutingResult } from './AutoRoutingEngine';

export class MEPRoutingSolver {

    /**
     * generating a complete run of Ducts/Pipes + Fittings from A to B.
     */
    static routeAndCreateElements(
        startPoint: THREE.Vector3,
        endPoint: THREE.Vector3,
        systemType: 'Duct' | 'Pipe' = 'Duct',
        diameterOrWidth: number = 300,
        height: number = 300 // For ducts
    ): BIMElement[] {
        const engine = new AutoRoutingEngine();

        // Find Path
        const result: RoutingResult = engine.findRoute(startPoint, endPoint, systemType);

        if (!result.success) {
            console.warn("Auto-route failed:", result.errorMessage);
            return [];
        }

        const elements: BIMElement[] = [];

        // Convert segments to BIM Elements
        result.path.forEach((segment, index) => {
            const id = uuidv4();

            // Create Linear Element
            if (systemType === 'Duct') {
                const duct: DuctElement = {
                    id,
                    category: 'Ducts',
                    type: 'Rectangular Duct',
                    parameters: {},
                    constraints: [],
                    geometryData: {
                        path: [[segment.start.x, segment.start.y, segment.start.z], [segment.end.x, segment.end.y, segment.end.z]],
                        width: diameterOrWidth,
                        height: height,
                        shape: 'Rectangular',
                        fittings: []
                    }
                };
                elements.push(duct);
            } else {
                const pipe: PipeElement = {
                    id,
                    category: 'Pipes',
                    type: 'Standard Pipe',
                    parameters: {},
                    constraints: [],
                    geometryData: {
                        path: [[segment.start.x, segment.start.y, segment.start.z], [segment.end.x, segment.end.y, segment.end.z]],
                        diameter: diameterOrWidth,
                        slope: 0,
                        fittings: []
                    }
                };
                elements.push(pipe);
            }

            // Create Fitting if needed. The routing engine flags a bend on
            // the segment that FOLLOWS the corner: fittingType belongs to
            // segment.start (junction with the previous segment).
            if (segment.fittingType && index > 0) {
                const fitting = this.createElbowInternal(
                    systemType,
                    segment.start,
                    result.path[index - 1].direction,
                    segment.direction,
                    diameterOrWidth,
                    height
                );
                if (fitting) elements.push(fitting);
            }
        });

        return elements;
    }

    /**
     * Create an elbow fitting between two vectors.
     */
    private static createElbowInternal(
        systemType: 'Duct' | 'Pipe',
        position: THREE.Vector3,
        dirIn: THREE.Vector3, // direction of incoming segment
        dirOut: THREE.Vector3, // direction of outgoing segment
        width: number,
        height: number
    ): BIMElement | null {
        // Angle change
        const angle = dirIn.angleTo(dirOut); // Radians
        const degrees = THREE.MathUtils.radToDeg(angle);

        const id = uuidv4();

        const fitting: BIMElement = {
            id: id,
            category: systemType === 'Duct' ? 'Duct Fittings' : 'Pipe Fittings',
            type: systemType === 'Duct' ? 'Rectangular Elbow' : 'Pipe Elbow',
            parameters: {
                'Angle': degrees,
                'Position': position.toArray()
            },
            constraints: [],
            geometryData: {
                // Mock geometry data for fitting
                position: position.toArray(),
                angle: degrees,
                width,
                height
            } as any
        };

        return fitting;
    }

    // Legacy method maintained for compatibility but delegating or deprecated
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    static createElbow(_segment1: BIMElement, _segment2: BIMElement, _intersection: THREE.Vector3): BIMElement | null {
        return null; // logic moved to routeAndCreateElements flow
    }
}
